<script setup>
import { usePlaylistStore } from "@/stores/playlist.js";
import { ref, watch, onBeforeMount, computed, onMounted, onBeforeUnmount } from 'vue';
import { enableScroll, disableScroll } from '@/Helpers/scroll.js'
import { resolveColourValue } from '@/Helpers/themeColourResolver';
import { createVideoStallWatchdog } from '@/Helpers/videoStallWatchdog';
import Bugsnag from "@bugsnag/js";
import "@mux/mux-player"

const props = defineProps({
    row: { type: Object, default: () => ({}) },
    isIntersecting: Boolean,
    brand: { type: Object, default: () => ({}) },
    shift: { type: Number, default: 0 },
    scormExport: Boolean,
})

const store = usePlaylistStore();
const muxPlayerRef = ref(null);
const currentTime = ref(0);
const subtitles = ref([]);
const currentSubtitle = ref('');
const lastSubtitleEndTime = ref(0);
const lastSubtitleText = ref('');
const isMobile = ref(false);
let teardownStallWatchdog = null;

// Detect if we're on a mobile device
onMounted(() => {
    isMobile.value = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});

// Parse SRT time format to seconds
function parseTimeToSeconds(timeStr) {
    const [time, milliseconds] = timeStr.split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds + parseInt(milliseconds) / 1000;
}

// Parse SRT content into subtitle objects
function parseSRT(srtContent) {
    if (!srtContent || !srtContent.trim()) return [];
        
    const blocks = srtContent.trim().split('\n\n');
    const parsedSubtitles = [];
        
    blocks.forEach(block => {
        const lines = block.trim().split('\n');
        if (lines.length >= 3) {
            const timingLine = lines[1];
            const [startTime, endTime] = timingLine.split(' --> ');
            const text = lines.slice(2).join('\n');
                
            parsedSubtitles.push({
                start: parseTimeToSeconds(startTime),
                end: parseTimeToSeconds(endTime),
                text: text
            });
        }
    });
        
    return parsedSubtitles;
}

// Find current subtitle based on video time with gap-filling logic
function getCurrentSubtitle(time) {
    if (subtitles.value.length === 0) {
        return '';
    }
        
    // First, check if there's an active subtitle at this time
    const activeSubtitle = subtitles.value.find(sub => time >= sub.start && time <= sub.end);
        
    if (activeSubtitle) {
        // Update tracking variables when we have an active subtitle
        lastSubtitleEndTime.value = activeSubtitle.end;
        lastSubtitleText.value = activeSubtitle.text;
        return activeSubtitle.text;
    }
        
    // No active subtitle - check if we should keep showing the last one
    if (lastSubtitleText.value && time > lastSubtitleEndTime.value) {
        // Check if we're within 1 second of the last subtitle ending
        const timeSinceLastSubtitle = time - lastSubtitleEndTime.value;
            
        if (timeSinceLastSubtitle <= 1.0) {
            // Check if there's a upcoming subtitle that would start soon
            const upcomingSubtitle = subtitles.value.find(sub => sub.start > time);
                
            if (!upcomingSubtitle || upcomingSubtitle.start > time) {
                // Keep showing the last subtitle
                return lastSubtitleText.value;
            }
        } else {
            // More than 1 second has passed, clear the last subtitle
            //lastSubtitleText.value = '';
            //lastSubtitleEndTime.value = 0;
        }
    }
        
    return '';
}

// Load subtitles from API
async function loadSubtitles() {
    if (!props.row.content.file_id) {
        return;
    }
        
    try {
        const response = await fetch(`/api/files/${props.row.content.file_id}/subtitles`);
        const data = await response.json();
            
        if (data.subtitles) {
            subtitles.value = parseSRT(data.subtitles);
        }
    } catch (error) {
        console.error('Error loading subtitles:', error);
    }
}

onBeforeMount(() => {
    if (props.scormExport) return;
    watch(
        () => props.isIntersecting,
        () => { 
            if (props.isIntersecting === false) {
                muxPlayerRef.value.pause()
                window.removeEventListener("orientationchange", checkScreenOrientation, false);
            } else {
                window.addEventListener("orientationchange", checkScreenOrientation, false);
            }
        }
    );
});

onMounted(() => {
    if (props.scormExport) return;
    if (!muxPlayerRef.value) return;

    // Detect playback freezes (e.g. the Microsoft Edge / network issue where the
    // first seconds play then playback hangs) and surface them to Bugsnag, which
    // captures browser/OS/URL so the affected environment is visible.
    teardownStallWatchdog = createVideoStallWatchdog(muxPlayerRef.value, (details) => {
        if (!Bugsnag.isStarted?.()) return;

        if (details.reason === 'recovered') {
            Bugsnag.leaveBreadcrumb?.('Mux video recovered after stall', details, 'state');
            return;
        }

        Bugsnag.notify(new Error('Mux video playback stalled'), (event) => {
            event.severity = details.reason === 'error' ? 'error' : 'warning';
            event.context = 'Mux video playback';
            event.addMetadata('video', details);
        });
    });
    onBeforeUnmount(() => {
        if (teardownStallWatchdog) {
            teardownStallWatchdog();
            teardownStallWatchdog = null;
        }
    });

    // Load subtitles if subtitles are enabled
    if (props.row.content.subtitles?.enabled) {
        loadSubtitles();
    }

    // Wait for the player to be fully initialised
    muxPlayerRef.value.addEventListener('playing', () => {
        const video = muxPlayerRef.value.media.nativeEl;
        for (let i = 0; i < video.textTracks.length; i++) {
            if (video.textTracks[i].kind === 'subtitles' || video.textTracks[i].kind === 'captions') {
                video.textTracks[i].mode = 'disabled';
            }
        }
    });

    // Track current time for subtitles - with mobile-specific handling
    const updateSubtitles = () => {
        currentTime.value = muxPlayerRef.value.currentTime || 0;
        const newSubtitle = getCurrentSubtitle(currentTime.value);
            
        if (newSubtitle !== currentSubtitle.value) {
            currentSubtitle.value = newSubtitle;
        }
    };

    muxPlayerRef.value.addEventListener('timeupdate', updateSubtitles);
        
    // Add more frequent updates for mobile devices
    if (isMobile.value) {
        const mobileUpdateInterval = setInterval(() => {
            if (muxPlayerRef.value && !muxPlayerRef.value.paused) {
                updateSubtitles();
            }
        }, 100); // Update every 100ms on mobile
            
        // Clean up interval when component unmounts
        onBeforeUnmount(() => {
            clearInterval(mobileUpdateInterval);
        });
    }

    // Reset subtitle tracking on seek/jump
    muxPlayerRef.value.addEventListener('seeked', () => {
        lastSubtitleText.value = '';
        lastSubtitleEndTime.value = 0;
        currentSubtitle.value = getCurrentSubtitle(currentTime.value);
    });

    // Reset subtitle tracking when video starts/restarts
    muxPlayerRef.value.addEventListener('play', () => {
        if (currentTime.value === 0) {
            lastSubtitleText.value = '';
            lastSubtitleEndTime.value = 0;
        }
    });

    // I need to watch for the video being in fullscreen mode
    muxPlayerRef.value.addEventListener('fullscreenchange', () => {
        if (document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
            store.isShowingFullscreenVideo = true;
            disableScroll();
        } else {
            store.isShowingFullscreenVideo = false;
            enableScroll();
        }
    });

});

function checkScreenOrientation() {
    var orientation = (window.screen.orientation || {}).type || window.screen.mozOrientation || window.screen.msOrientation;

    //console.log('Screen orientation changed:', orientation);
    if (orientation === undefined) return;


    if (orientation.startsWith('portrait')) {
        //console.log('Portrait mode detected');
        revertVideoFullscreen();
    } else if (orientation.startsWith('landscape')) {
        //console.log('Landscape mode detected');
        //setVideoFullscreen();
    }
}

function setVideoFullscreen() {
    const elem = muxPlayerRef.value.media.nativeEl;
    if (elem.requestFullscreen) {
        elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
    }

    // check if the video is in fullscreen mode
    if (document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
        store.isShowingFullscreenVideo = true;
    } else {
        store.isShowingFullscreenVideo = false;
    }
}

function revertVideoFullscreen() {
    const elem = muxPlayerRef.value.media.nativeEl;
    // Calling exitFullscreen when nothing is fullscreen throws "Not in
    // fullscreen" on some browsers; only call it if we actually are.
    const isFullscreen = document.fullscreenElement
        || document.mozFullScreenElement
        || document.webkitFullscreenElement
        || document.msFullscreenElement;
    if (isFullscreen) {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
            /* Firefox */
            document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
            /* Chrome, Safari and Opera */
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            /* IE/Edge */
            document.msExitFullscreen();
        }
    }

    // check if the video is in fullscreen mode
    if (document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
        store.isShowingFullscreenVideo = true;
    } else {
        store.isShowingFullscreenVideo = false;
    }
}

const borderColour = computed(() => resolveColourValue(props.row?.content?.borderColour, props.brand, props.shift) || '');

const borderWidth = computed(() => {
    return props.row.content.borderWidth ? props.row.content.borderWidth : '0';
});

const borderRadius = computed(() => {
    return props.row.content.borderRadius ? props.row.content.borderRadius : '0';
});

function resolveSubtitleColour(field, fallback) {
    return resolveColourValue(field, props.brand, props.shift) || fallback;
}

// Subtitle styling computed properties
const subtitleBackgroundColor = computed(() => {
    if (!props.row.content.subtitles) return 'transparent';

    const style = props.row.content.subtitles.backgroundStyle;
    const bgColor = resolveSubtitleColour(props.row.content.subtitles.backgroundColor, '#252527');

    switch (style) {
    case 'Solid':
        return bgColor;
    case 'Transparent':
        // Convert hex to rgba with 50% opacity
        const hex = bgColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, 0.5)`;
    case 'Outline':
    case 'None':
    default:
        return 'transparent';
    }
});

const subtitleBorder = computed(() => {
    if (!props.row.content.subtitles) return 'none';

    const style = props.row.content.subtitles.backgroundStyle;
    const bgColor = resolveSubtitleColour(props.row.content.subtitles.backgroundColor, '#252527');

    return style === 'Outline' ? `2px solid ${bgColor}` : 'none';
});

const subtitlePlacement = computed(() => {
    return props.row.content.subtitles?.placement || 90;
});

// Get complete subtitle text styling including new properties
const subtitleTextStyling = computed(() => {
    const subtitles = props.row.content.subtitles;
    if (!subtitles) return {};
        
    // Ensure font fallback for mobile devices
    const fontFamily = subtitles.font || 'Paralucent';
    const safeFontFamily = isMobile.value 
        ? `${fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif`
        : fontFamily;
        
    let styling = {
        color: resolveSubtitleColour(subtitles.textColor, '#FFFFFF'),
        fontFamily: safeFontFamily,
        fontSize: `${subtitles.fontSize || 16}px`,
        fontWeight: subtitles.fontWeight || 'normal',
        lineHeight: '1.2em',
        textAlign: 'center',
        // Force minimum contrast for mobile
        minHeight: isMobile.value ? '20px' : 'auto'
    };

    // Add text stroke if enabled
    if (subtitles.textStrokeSize > 0) {
        const strokeColour = resolveSubtitleColour(subtitles.textStrokeColor, '#000000');
        styling.webkitTextStroke = `${subtitles.textStrokeSize}px ${strokeColour}`;
    }
        
    // Add text shadow if not 'none'
    if (subtitles.textShadow && subtitles.textShadow !== 'none') {
        styling.textShadow = subtitles.textShadow;
    } else if (subtitles.backgroundStyle === 'None' || isMobile.value) {
        // Enhanced shadow for mobile readability
        styling.textShadow = isMobile.value 
            ? '2px 2px 4px rgba(0,0,0,0.9), 0px 0px 2px rgba(0,0,0,0.9)'
            : '1px 1px 2px rgba(0,0,0,0.8)';
    }
        
    return styling;
});
    
const videoClasses = computed(() => {
    // With fit=cover we want the player to fill the container so the inner
    // video can crop to fill; with fit=contain we keep the natural aspect
    // ratio (set in videoStyles) so the player letterboxes inside the row.
    return props.row.content.fit === 'cover' ? 'w-full h-full' : 'mx-auto h-full';
});

const videoStyles = computed(() => {
    const fitContain = props.row.content.fit !== 'cover';
    return `
            ${fitContain ? `aspect-ratio: ${props.row.content.aspect_ratio};` : ''}
            object-fit: ${props.row.content.fit};
            object-position: ${props.row.content.position};
            --media-object-fit: ${props.row.content.fit};
            --media-object-position: ${props.row.content.position};
        `;
});
</script>
<template>
    <div 
        class="w-full relative min-h-8" 
        :style="{
            'height': (typeof row.content.height === 'number' ? row.content.height + '%' : (row.content.height === 'h-full' ? '100%' : row.content.height === 'h-3/4' ? '75%' : row.content.height === 'h-1/2' ? '50%' : row.content.height === 'h-1/4' ? '25%' : '50%')),
            'border-color': borderColour,
            'border-width': borderWidth + 'px',
            'border-radius': borderRadius + '%'
        }"
    >
        <video 
            v-if="scormExport" 
            :class="videoClasses"
            :style="videoStyles"
            controls
        >
            <source :src="row.content.videoSrc" type="video/mp4">
        </video>
        <mux-player 
            v-else 
            ref="muxPlayerRef"
            :class="videoClasses" 
            :style="videoStyles"
            stream-type="on-demand"
            :playback-id="row.content.mux_playback_id"
            :primary-color="brand.colour"
            accent-color="#F4E6DF"
            thumbnail-time="1"
            :metadata-video-title="row.content.title"
            :metadata-viewer-user-id="'sixty-builder'"
            preload="auto"
            default-hidden-captions
        />
        
        <!-- Subtitle Overlay -->
        <div 
            v-if="row.content.subtitles?.enabled && currentSubtitle"
            class="absolute inset-x-0 pointer-events-none flex justify-center px-4 z-50"
            :style="{
                bottom: `${100 - subtitlePlacement}%`,
                transform: 'translateY(50%)',
                position: 'absolute',
                width: '100%',
                left: '0',
                right: '0'
            }"
        >
            <div 
                class="px-2 py-1 rounded text-center max-w-full subtitle-text"
                :style="{
                    ...subtitleTextStyling,
                    backgroundColor: subtitleBackgroundColor,
                    border: subtitleBorder,
                    maxWidth: '90%',
                    wordWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    zIndex: '1000'
                }"
            >
                {{ currentSubtitle }}
            </div>
        </div>
    </div>
</template>
<style scoped>
    mux-player {
        --center-seek-backward-button: none;
        --center-seek-forward-button: none;
        --airplay-button: none;
        --pip-button: none;
        --cast-button: none;
        --playback-rate-button: none;
    }

    /* Mobile-specific subtitle fixes */
    .subtitle-text {
        /* Ensure text is visible on mobile */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        /* Force hardware acceleration for better mobile performance */
        transform: translateZ(0);
        will-change: contents;
        /* Ensure proper stacking on mobile */
        position: relative;
        z-index: 1000;
    }

    /* iOS Safari specific fixes */
    @supports (-webkit-touch-callout: none) {
        .subtitle-text {
            /* Additional iOS fixes */
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
        }
    }

    /* Ensure video container doesn't interfere with subtitles */
    .w-full.relative.min-h-8 {
        /* Create stacking context */
        isolation: isolate;
    }
</style>